import React from "react";
import {
  useHistory,
  useRouteMatch
} from "react-router-dom";
import styled, { css } from "styled-components";
import _ from "lodash";
import { useMappedState } from "redux-react-hook";

import { Text } from "src/library/components/Text";
import { useOrder } from "src/core/orders/hooks";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import MainContentWrapper from "src/core/shared/components/Layout/MainContentWrapper";
import { PageProps } from "src/pages/Router/routes";
import PayOrderSummery from "src/core/orders/components/PayOrderSummary/PayOrderSummary";
import SelectedPaymentCard from "src/modules/payment/components/PaymentMethodCard/SelectedPaymentMethodCard";
import { Address } from "src/library/components/Address";
import { IPaymentCard } from "src/modules/payment/api/payment.api";
import { Layout } from "src/core/shared/components/Layout";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Button } from "src/library/components/Button";
import { useDrop } from "src/core/drops/hooks";
import { IStore } from "src/core/shared/store";

interface OrderCheckoutPageProps {
  orderId: string;
}

const OrderCheckoutPage: React.FunctionComponent<PageProps<OrderCheckoutPageProps>> = ({ ...props }) => {
  const { terminology } = props;
  const [ selectedPaymentMethodCard, setSelectedPaymentMethodCard ] = React.useState<IPaymentCard | null>(null);
  const match = useRouteMatch<{ orderId?: string }>();
  const [ { drop } ] = useDrop();
  const orderId = React.useMemo(() => match.params.orderId ? parseInt(match.params.orderId as string, 0) : null, [ match.params.orderId ]);
  const history = useHistory();
  const { masqueradeMode } = useMappedState(({ global }: IStore) => ({ masqueradeMode: global.masqueradeMode }));

  const isAllowedPayments = React.useMemo(() => {
    if (drop?.groupedSettings.require_payment_method?.value.required === false || masqueradeMode?.active) {
      return false;
    }

    return true;
  }, [ drop?.groupedSettings.require_payment_method?.value.required ]);

  const [
    {
      isLoading: isLoadingOrder,
      currentOrder
    }
  ] = useOrder(orderId, null, true);

  if (isLoadingOrder) {
    return (
      <MainContentWrapper>
        <LoadingSpinner type="circular" />
      </MainContentWrapper>
    );
  }

  return (
    <Layout {...props}>
      <MainContentWrapper>
        <StyledLayoutWrapper>
          {!isAllowedPayments ? (
            <div>
              <ErrorNotice
                type="error"
                message="Your account is not set up to accept payment by credit or debit card. Your order has been placed, you will be invoiced at a later date."
              />

              <div>
                <Button onClick={() => history.push("/dashboard")}>
                  Back to dashboard
                </Button>
              </div>
            </div>
          ) : (
            <>

              <div className="column pay-with-wrapper">
                <Text as="h2">
                  Pay With
                </Text>

                <SelectedPaymentCard
                  selectedPaymentMethodCard={selectedPaymentMethodCard}
                  setSelectedPaymentMethodCard={setSelectedPaymentMethodCard}
                  terminology={terminology}
                  defaultPaymentMethodId={currentOrder?.data?.branch?.defaultPaymentCardId}
                />

                <Text as="h2">
                  Delivery Address
                </Text>

                <div className="address-row">
                  <Text>
                    {`${_.startCase(terminology.drop.singular.text)} Code: ${currentOrder?.data?.branch?.branchNumber}`}
                  </Text>
                </div>

                <div className="branch-name">
                  <Text as="h4">
                    {currentOrder?.data?.branch?.name}
                  </Text>
                </div>

                <div className="address">
                  <Address address={currentOrder?.data?.branch?.address} />
                </div>
              </div>

              <div className="column order-summary">
                {currentOrder.data && (
                  <PayOrderSummery
                    terminology={terminology}
                    order={currentOrder.data}
                    selectedPaymentCard={selectedPaymentMethodCard}
                  />
                )}
              </div>
            </>
          )}

        </StyledLayoutWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledLayoutWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;

    .order-summary {
      margin-top: ${theme.spacing.margins.lg};
      max-width: 390px;
    }

    .branch-name > :nth-child(1) {
      margin: ${theme.spacing.margins.xs} 0;
    }

    .address {
      font-size: ${theme.fonts.sizes.p};
      
      span {
        margin: ${theme.spacing.margins.xs} 0;
      }
    }

    @media (min-width: 1000px) {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      width: 100%;
      margin-top: 0;

      .column {
        display: flex;
        flex-direction: column;
        flex-basis: 100%;
      }
  
      .pay-with-wrapper {
        flex: 1;
      }
  
      .order-summary {
        flex: 1;
        flex-wrap: wrap;
        align-content: flex-end;
      }
    }
  `}
`;

export default OrderCheckoutPage;
